import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, Chip, CircularProgress, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, MenuItem, Grid,
} from '@mui/material';
import { EventNote, Add, Edit, Delete, ArrowBack, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { sym, locale } from '../utils/currency';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useNavigate } from 'react-router-dom';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CATEGORIES = ['AGM', 'Visiting Day', 'Sports', 'Closing Day', 'Prize Giving', 'Meeting', 'Trip', 'Exam', 'Other'];
const STATUSES   = ['Scheduled', 'Cancelled', 'Completed'];

const STATUS_COLOR = { Scheduled: 'info', Cancelled: 'error', Completed: 'success' };

const emptyForm = {
  title: '', category: 'Other', date: '', endDate: '',
  venue: '', charge: 0, description: '', status: 'Scheduled',
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const toDateInput = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().split('T')[0]; } catch { return ''; }
};

const EventsList = () => {
  const navigate = useNavigate();
  // Class teachers get a read-only view (they can see events but not manage them)
  const isClassTeacher = (JSON.parse(localStorage.getItem('user') || '{}')).role === 'classTeacher';
  const [events,     setEvents]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [msg,        setMsg]        = useState('');
  const [dlg,        setDlg]        = useState(false);
  const [editing,    setEditing]    = useState(null);   // event being edited
  const [form,       setForm]       = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dlg, value: form, onClose: () => setDlg(false) });
  const [saving,     setSaving]     = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/academics/events?all=true`, { headers: hdrs() });
      setEvents(r.data.data || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load events');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const openNew = () => { setEditing(null); setForm(emptyForm); setDlg(true); };

  const openEdit = (evt) => {
    setEditing(evt);
    setForm({
      title:       evt.title       || '',
      category:    evt.category    || 'Other',
      date:        toDateInput(evt.date),
      endDate:     toDateInput(evt.endDate),
      venue:       evt.venue       || '',
      charge:      evt.charge      || 0,
      description: evt.description || '',
      status:      evt.status      || 'Scheduled',
    });
    setDlg(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const save = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.date)         { setError('Date is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, charge: Number(form.charge) || 0, endDate: form.endDate || null };
      if (editing) {
        await axios.put(`${API}/api/academics/events/${editing._id}`, payload, { headers: hdrs() });
        flash('Event updated');
      } else {
        await axios.post(`${API}/api/academics/events`, payload, { headers: hdrs() });
        flash('Event created — parents will see it on their portal');
      }
      setDlg(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    const evt = confirmDel; setConfirmDel(null);
    try {
      await axios.delete(`${API}/api/academics/events/${evt._id}`, { headers: hdrs() });
      flash('Event deleted');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={isClassTeacher ? 'Back to Class Teacher Centre' : 'Back to Academics'}>
              <IconButton onClick={() => navigate(isClassTeacher ? '/class-teacher-centre' : '/academics')} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <EventNote color="primary" sx={{ fontSize: 26, ml: 0.5 }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="primary">School Events</Typography>
              <Typography variant="caption" color="text.secondary">
                AGMs, visiting days, sports, ceremonies — published to the parent portal
              </Typography>
            </Box>
          </Box>
          {!isClassTeacher && <Button variant="contained" startIcon={<Add />} onClick={openNew}>New Event</Button>}
        </Box>

        {msg   && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
        {error && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box textAlign="center" py={8}><CircularProgress /></Box>
        ) : events.length === 0 ? (
          <Box textAlign="center" py={8}>
            <EventNote sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No events yet. Create one to notify parents.</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: 1, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, py: 1.5 }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Venue</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Charge ({sym()})</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  {!isClassTeacher && <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map(evt => (
                  <TableRow key={evt._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{evt.title}</Typography>
                      {evt.description && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block" maxWidth={280}>
                          {evt.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip label={evt.category} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {fmtDate(evt.date)}{evt.endDate ? ` – ${fmtDate(evt.endDate)}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{evt.venue || '—'}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" fontWeight={600}>
                        {evt.charge > 0 ? evt.charge.toLocaleString() : 'Free'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={evt.status} size="small" color={STATUS_COLOR[evt.status] || 'default'} />
                    </TableCell>
                    {!isClassTeacher && (
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openEdit(evt)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setConfirmDel(evt)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Create / edit dialog */}
      {discardDialog}
      <Dialog open={dlg} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Event' : 'New Event'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField size="small" label="Title *" name="title" value={form.title} onChange={handle} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select size="small" label="Category" name="category" value={form.category} onChange={handle} fullWidth>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="Date *" name="date" type="date" value={form.date}
                onChange={handle} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField size="small" label="End Date" name="endDate" type="date" value={form.endDate}
                onChange={handle} fullWidth InputLabelProps={{ shrink: true }}
                helperText="Optional — for multi-day events" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField size="small" label="Venue" name="venue" value={form.venue} onChange={handle} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField size="small" label={`Charge (${sym()})`} name="charge" type="number" value={form.charge}
                onChange={handle} fullWidth helperText="0 = free" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" label="Description" name="description" value={form.description}
                onChange={handle} fullWidth multiline rows={3}
                helperText="What parents should know — agenda, what to bring, etc." />
            </Grid>
            {editing && (
              <Grid item xs={12} sm={6}>
                <TextField select size="small" label="Status" name="status" value={form.status} onChange={handle} fullWidth>
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Event
        </DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{confirmDel?.title}</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            It will disappear from the parent portal. To keep it visible but flagged,
            set its status to Cancelled instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsList;
